/* eslint-disable max-len */
import React, { useEffect } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import PropTypes from 'prop-types';
import { useImpression } from '@thd-olt-component-react/impression';
import { Card, CardBody } from '@one-thd/sui-atomic-components';
import { ListSettings, EmailList } from '@thd-olt-component-react/list-forms';
import ListHeader from './sub-components/ListHeader';
import ListImages from './sub-components/ListImages';
import ListTilePlaceholder from './ListTilePlaceholder';

const {
  func, shape, string, arrayOf, number, bool
} = PropTypes;

export const ListTile = ({
  listId,
  data,
  onDelete,
  position,
  iconSize,
  disableLink = false,
  disableIconAction = false,
  numberOfThumbnails = 2,
  enableSharedIcon = false,
  hideSubHeader = false,
  maxWidth,
  disableThumbnailsLink = false,
  viewMode = 'grid',
  impressionName = 'ListTile',
  impressionComponent = 'ListTile',
  isDashboardSnapshot = false,
  iconType,
  onClick
}) => {

  const list = data;
  const isEmptyList = list?.itemCount === 0;
  // Grow CardBody for any empty list, so grid view's "Add materials" panel can fill a
  // taller card (e.g. Dashboard Snapshot's shared height floor).
  const shouldGrowBody = isEmptyList;
  // Grid view's empty state. Thumbnails view's "Complete your list" Alert keeps the header margin.
  const isAddMaterialsState = isEmptyList && viewMode !== 'thumbnails';

  const maxWidthClass = {
    xs: 'sui-max-w-xs',
    sm: 'sui-max-w-sm',
    md: 'sui-max-w-md',
    lg: 'sui-max-w-lg',
    xl: 'sui-max-w-xl',
    '2xl': 'sui-max-w-2xl'
  }[maxWidth] || '';

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('list-tile.ready');
  }, []);

  // Fired from the name and thumbnail links only; the consumer decides what to track.
  const handleNameClick = () => onClick?.(listId, position, 'name');
  const handleImageClick = () => onClick?.(listId, position, 'image');

  const onDeleteConfirmation = () => {
    if (onDelete) {
      onDelete({ listId });
    }
  };

  const handleEmptyListNavigation = () => {
    if (list?.url) {
      window.location.href = list.url;
    }
  };

  const impressionContextData = useImpression({
    data: {
      id: listId,
      name: impressionName,
      component: impressionComponent,
      position: position + 1,
      type: 'content'
    }
  });

  if (!list) {
    return <ListTilePlaceholder numberOfThumbnails={numberOfThumbnails} />;
  }

  if (!list?.url) {
    return null;
  }

  const listTileRef = impressionContextData.ref;
  const listTileClickId = impressionContextData.clickID;

  const getActionMenuContent = () => {
    if (disableIconAction) {
      return null;
    }

    if (enableSharedIcon) {
      if (list?.itemCount > 0) {
        return (
          <div className="sui-rounded-base sui-cursor-pointer">
            <EmailList listId={list.id} iconSize={iconSize} shareIcon />
          </div>
        );
      }
      return null;
    }

    return (
      <ListSettings
        iconSize={iconSize}
        list={list}
        onDelete={onDeleteConfirmation}
        isEmptyList={isEmptyList}
        iconType={iconType}
      />
    );
  };

  const actionMenuButton = (
    <>
      {getActionMenuContent()}
    </>
  );

  return (
    <div
      className="sui-w-full sui-h-full"
      ref={listTileRef}
      // eslint-disable-next-line react/no-unknown-property
      clickid={listTileClickId}
      data-component="ListTile"
      data-testid="list-tile"
    >
      <Card
        className={`sui-w-full sui-h-full [&>div]:sui-h-full ${maxWidthClass} [&>div]:sui-justify-between ${isDashboardSnapshot ? 'sui-rounded-md' : ''}`}
      >
        {/*
          [&>div]:sui-justify-between (see Card's className) only supplies a gap between this
          header row and CardBody when the card has leftover height (e.g. a shorter neighbor
          in the same Filmstrip row) — sui-mb-4 guarantees a minimum gap even when the header
          fills the card on its own. CardBody itself takes no className, so the margin goes
          on this row instead. The "Add materials" state drops it: CardBody grows to fill the
          card there, and CardContent's own sui-gap-4 alone matches the "Expand Add Materials
          Panel" comps.
        */}
        <div className={`sui-flex sui-justify-between sui-items-start sui-gap-2 ${isAddMaterialsState ? '' : 'sui-mb-4'}`}>
          <ListHeader
            name={list?.name}
            url={list?.url}
            itemCount={list?.itemCount}
            hideSubHeader={hideSubHeader}
            disableLink={disableLink}
            viewMode={viewMode}
            onNameClick={onClick ? handleNameClick : undefined}
          />
          {actionMenuButton}
        </div>
        <CardBody disableGrow={!shouldGrowBody}>
          <ListImages
            images={list?.images}
            itemCount={list?.itemCount}
            numberOfThumbnails={numberOfThumbnails}
            url={list?.url}
            disableThumbnailsLink={disableThumbnailsLink}
            handleEmptyListNavigation={handleEmptyListNavigation}
            viewMode={viewMode}
            onImageClick={onClick ? handleImageClick : undefined}
          />
        </CardBody>
      </Card>
    </div>
  );
};

ListTile.displayName = 'ListTile';

ListTile.defaultProps = {
  onDelete: null,
  data: null,
  position: 0,
  iconSize: 'small',
  disableLink: false,
  disableIconAction: false,
  numberOfThumbnails: 2,
  enableSharedIcon: false,
  hideSubHeader: false,
  maxWidth: null,
  disableThumbnailsLink: false,
  viewMode: 'grid',
  impressionName: 'ListTile',
  impressionComponent: 'ListTile',
  isDashboardSnapshot: false,
  iconType: 'settings',
  onClick: undefined
};

ListTile.propTypes = {
  listId: string.isRequired,
  onDelete: func,
  iconSize: string,
  data: shape({
    id: string,
    name: string,
    url: string,
    itemCount: number,
    images: arrayOf(shape({
      url: string
    }))
  }),
  position: number,
  disableLink: bool,
  disableIconAction: bool,
  numberOfThumbnails: number,
  enableSharedIcon: bool,
  hideSubHeader: bool,
  maxWidth: string,
  disableThumbnailsLink: bool,
  viewMode: string,
  impressionName: string,
  impressionComponent: string,
  /** Set true when rendering inside Dashboard Snapshot, to use its 4px corner radius. */
  isDashboardSnapshot: bool,
  iconType: string,
  /**
   * Optional. Called once when the list name or a thumbnail link is clicked, as
   * `onClick(listId, position, source)` where `source` is 'name' or 'image'. Navigation is
   * not prevented. Not called for empty-state links or when the links are disabled.
   */
  onClick: func
};

ListTile.dataModel = extend(ListSettings);
